import { getFormatter } from "./formatterCache";
import { isHindi } from "./uiLocale";

export type DateFormatOptions = {
  showDate?: boolean;
  showTime?: boolean;
  relative?: boolean;
  // Day-first, zero-padded, no ordinal suffix: "01 July 2026". Opt-in, so the
  // global useOrdinal config keeps driving every existing caller.
  dayFirst?: boolean;
  // Abbreviated month and no ordinal suffix: "Sep 2, 2026" rather than
  // "September 2nd 2026". Opt-in, for places where the date sits in a
  // fixed-width container — the workflow timeline's step cards, where the
  // full form overflowed the card.
  shortMonth?: boolean;
};

function getOrdinal(day: number) {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function parseInput(input?: string | number | Date) {
  if (!input) return null;

  if (input instanceof Date) return input;
  if (typeof input === "number") return new Date(input);

  if (typeof input === "string") {
    if (/^\d{2}\/\d{2}\/\d{4}/.test(input)) {
      const [day, month, year] = input.split(/[\/\s]/);
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    return new Date(input);
  }

  return null;
}

export function formatDateTime(
  input: string | number | Date | undefined,
  globalConfig: {
    locale: string;
    timezone: string;
    use12Hour: boolean;
    showSeconds: boolean;
    useOrdinal: boolean;
  },
  options: DateFormatOptions = {},
) {
  const date = parseInput(input);
  if (!date || isNaN(date.getTime())) return "—";

  const { timezone, use12Hour, showSeconds, useOrdinal } = globalConfig;
  // In Hindi the words follow the UI language rather than the browser:
  // "30 सितंबर 2026", "4 दिन पहले". English keeps the configured locale.
  const hindi = isHindi();
  const locale = hindi ? "hi-IN" : globalConfig.locale;

  const {
    showDate = true,
    showTime = true,
    relative = false,
    dayFirst = false,
    shortMonth = false,
  } = options;

  // 🔥 Relative time
  if (relative) {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      numeric: "auto",
    });

    const diff = (date.getTime() - Date.now()) / 1000;

    const divisions = [
      { amount: 60, name: "seconds" },
      { amount: 60, name: "minutes" },
      { amount: 24, name: "hours" },
      { amount: 7, name: "days" },
      { amount: 4.34524, name: "weeks" },
      { amount: 12, name: "months" },
      { amount: Infinity, name: "years" },
    ];

    let duration = diff;

    for (let i = 0; i < divisions.length; i++) {
      if (Math.abs(duration) < divisions[i].amount) {
        return rtf.format(
          Math.round(duration),
          divisions[i].name as Intl.RelativeTimeFormatUnit,
        );
      }
      duration /= divisions[i].amount;
    }
  }

  let datePart = "";
  let timePart = "";

  if (showDate) {
    const day = date.getDate();
    const month = getFormatter(locale, {
      month: shortMonth ? "short" : "long",
      timeZone: timezone,
    }).format(date);

    const year = date.getFullYear();

    if (dayFirst || hindi) {
      // Hindi always reads day-first and has no ordinal suffix; dayFirst
      // keeps its zero-padding.
      datePart = `${dayFirst ? String(day).padStart(2, "0") : day} ${month} ${year}`;
    } else {
      // The ordinal suffix is dropped alongside the abbreviated month: the
      // point of shortMonth is to fit, and "Sep 2nd 2026" reads as a mismatch
      // of registers next to "Sep 2, 2026".
      datePart =
        useOrdinal && !shortMonth
          ? `${month} ${day}${getOrdinal(day)} ${year}`
          : `${month} ${day}, ${year}`;
    }
  }

  if (showTime) {
    timePart = getFormatter(locale, {
      hour: "2-digit",
      minute: "2-digit",
      second: showSeconds ? "2-digit" : undefined,
      hour12: use12Hour,
      timeZone: timezone,
    })
      .format(date)
      .toLowerCase();
  }

  if (showDate && showTime) return `${datePart}, ${timePart}`;
  if (showDate) return datePart;
  if (showTime) return timePart;

  return "—";
}

/*== Example Usage ===*/
/* const format = useFormatDate();

format(item.createdAt);
// January 29th 2026, 05:12:11 pm

format(item.createdAt, { showTime: false });
// January 29th 2026

format(item.createdAt, { relative: true });
// 2 hours ago

format(item.createdAt, { showDate: false });
// 05:12:11 pm */
